import {
  Button,
  Card,
  Divider,
  Form,
  message,
  Modal,
  Skeleton,
  Spin,
  Typography,
} from "antd";
import LayoutCss from "layout/layout.module.scss";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import BillingApproval from "redux/models/billingApproval";
import { selectSelectedClientIds } from "redux/slices/globalSelectorSlice";
import { selectUser } from "redux/slices/userSlice";
import K from "utilities/constants";
import { isPermissionPresent } from "utilities/generalUtility";
import { displayDollar } from "utilities/tableUtility";
import { Collapse } from "common/components/collapse/collapse";
import { ExportDataButton } from "common/components/ExportDataButton";
import Export from "redux/models/export";
import CreateInvoice from "./createInvoice";
import CreateJobInvoice from "./createJobInvoice/createJobInvoice";
import OfferedSalaryCalculations from "./offeredSalaryCalculation";
import "./submitToBilling.scss";
import "./billing-collapse.scss";
import { JobBillingsTable } from "./billingComponents/JobBillingsTable";
import { ClientBillingsTable } from "./billingComponents/ClientBillingsTable";

const { Title } = Typography;
const {
  Invoice: { Type },
} = K;
const keys = {
  jobTotal: 0,
  clientTotal: 0,
  isSameTypeJobs: true,
  isSameTypeClients: true,
  selectedJobBillingRows: [],
  selectedClientBillingRows: [],
  selectedJobBillingRowsData: [],
  jobSelectAll: false,
  clientSelectAll: false,
  jobSelectedNodes: [],
  clientSelectedNodes: [],
  offerSalaryMissingRows: [],
};

export default function BillingComponent({
  clientId,
  clientName,
  onBillingSelected,
}) {
  const invoicePayloadRef = useRef(null);
  const [form] = Form.useForm();
  const [jobForm] = Form.useForm();

  const [salaryForm] = Form.useForm();

  const [pageStates, setPageStates] = useState({
    listing: [],
    checkedRows: { ...keys },
    isDueDateModalVisible: false,
    isJobDueDateModalVisible: false,
    isOfferedSalaryModalVisible: false,
    selectedClientIndex: null,
    jobLoading: false,
    clientLoading: false,
  });
  const selectedClientIds = useSelector(selectSelectedClientIds);
  const userSlice = useSelector(selectUser);

  const [rowData, setRowData] = useState([]);
  const [isOpen, setIsOpen] = useState(
    selectedClientIds[0] === clientId || userSlice.clients[0].id === clientId,
  );
  const [loading, setLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);
  const [totalLoading, setTotalLoading] = useState({
    client: false,
    job: false,
  });
  const [isExportDisabled, setIsExportDisabled] = useState(true);
  const jobGridRef = useRef(null);

  const handleInvoice = (billType, index) => {
    if (pageStates.checkedRows.offerSalaryMissingRows > 0) {
      setPageStates((prev) => ({
        ...prev,
        isOfferedSalaryModalVisible: true,
        selectedClientIndex: index,
      }));
    } else {
      generateInvoice(billType, index);
    }
  };

  const generateInvoice = async (billType, index) => {
    invoicePayloadRef.current = {
      index,
      clientId,
      type: billType,
      submittalBillIds:
        Type.Client === billType
          ? pageStates.checkedRows.clientSelectedNodes
          : pageStates.checkedRows.jobSelectedNodes,
      selectAll:
        Type.Client === billType
          ? pageStates.checkedRows.clientSelectAll
          : pageStates.checkedRows.jobSelectAll,
    };

    if (billType === Type.Client) {
      form.resetFields();
      setPageStates((prev) => ({ ...prev, isDueDateModalVisible: true }));
    } else {
      jobForm.resetFields();
      setJobLoading(true);
      setPageStates((prev) => ({ ...prev, isJobDueDateModalVisible: true }));
    }
  };

  const reCalculateBilling = async (
    candidateJobEntryId,
    billingRuleEntryId,
    salary,
  ) => {
    try {
      await BillingApproval.submitReCalculateBilling(
        billingRuleEntryId,
        candidateJobEntryId,
        salary,
      );
      setPageStates((prev) => ({
        ...prev,
        jobLoading: true,
      }));

      const selectedJobRowsData = res[0].submittedJobBills.filter((dt) =>
        pageStates?.checkedRows.selectedJobBillingRows.includes(dt.id),
      );

      const selectedJobRows = selectedJobRowsData?.map(({ id }) => id) ?? [];

      const updatedCheckedRowsData = {
        ...pageStates?.checkedRows,
        selectedJobBillingRowsData: selectedJobRowsData,
        selectedJobBillingRows: selectedJobRows,
      };

      setPageStates((prev) => ({
        ...prev,
        listing: res[0],
        checkedRows: { ...updatedCheckedRowsData },
        jobLoading: false,
        isOfferedSalaryModalVisible: false,
      }));
    } catch (error) {
      console.log("Error ", error);
    }
  };

  const onCancelOfferSalary = () => {
    const updatedRowData = rowData.map((item) => {
      return {
        ...item,
        enteredSalary: null,
        updatedBill: null,
      };
    });

    setRowData([...updatedRowData]);
  };

  const fetchClientTitle = () => {
    return userSlice.clients.find((item) => item.id === clientId).name;
  };

  const toggleCollapse = () => {
    setIsOpen(!isOpen);
  };

  const onExport = async () => {
    try {
      const gridRef = jobGridRef;
      const sortColumn = gridRef.current.columnApi
        .getColumnState()
        .find((c) => c.sort);
      const filterModel = gridRef.current.api.getFilterModel();

      const params = {
        sortBy: sortColumn?.colId,
        sortOrder: sortColumn?.sort,
        filterBy: filterModel?.isManual && "isManual",
        filterValues: filterModel?.isManual?.values,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      await Export.getReportJobBillings(clientId, params);
      message.success(
        "Submit to Billings export request successfully received! Check your email shortly",
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Collapse
        defaultActiveKey={selectedClientIds[0] || userSlice.clients[0].id}
        expandIconPosition="right"
      >
        <Collapse.Panel
          header={fetchClientTitle()}
          key={clientId}
          className="billing-approval-header"
          onClick={toggleCollapse}
        >
          <Spin
            onClick={(e) => e.stopPropagation()}
            spinning={pageStates.clientLoading}
            className="billing-component"
          >
            <Card
              bodyStyle={{ padding: 0 }}
              headStyle={{ padding: 0 }}
              className={`clientBillingCard  table-pagination collapse-card ${LayoutCss.appCard}`}
              bordered={false}
              title="Client Billing"
              extra={
                <div className="clientBilingBtn">
                  <Title level={5} className="mb-0">
                    Total:&nbsp;{" "}
                    { displayDollar(pageStates.checkedRows?.clientTotal) }
                  </Title>

                  {isPermissionPresent(
                    K.Permissions.CreateInvoices,
                    userSlice.roles,
                  ) && (
                    <Button
                      type="primary"
                      className={LayoutCss.appHeaderBtn}
                      onClick={() => {
                        generateInvoice(Type.Client, pageStates.listing.id);
                      }}
                      disabled={
                        pageStates.checkedRows?.isSameTypeClients ||
                        totalLoading.client
                      }
                    >
                      Create Invoice
                    </Button>
                  )}
                </div>
              }
            >
              <ClientBillingsTable
                clientId={clientId}
                clientName={clientName}
                pageStates={pageStates}
                setPageStates={setPageStates}
                setTotalLoading={setTotalLoading}
                onBillingSelected={onBillingSelected}
              />
            </Card>
          </Spin>

          <Divider />

          <Spin
            onClick={(e) => e.stopPropagation()}
            spinning={pageStates.jobLoading}
            className="billing-component"
          >
            <Card
              bodyStyle={{ padding: 0 }}
              headStyle={{ padding: 0 }}
              title="Job Billing"
              extra={
                <div className="clientBilingBtn">
                  <Title className="mb-0" level={5} style={{ display: "flex" }}>
                    Total:&nbsp;{" "}
                    { displayDollar(pageStates.checkedRows?.jobTotal) }
                  </Title>
                  {isPermissionPresent(
                    K.Permissions.CreateInvoices,
                    userSlice.roles,
                  ) && (
                    <Button
                      type="primary"
                      className={LayoutCss.appHeaderBtn}
                      onClick={() =>
                        handleInvoice(Type.Job, pageStates?.listing.id)
                      }
                      disabled={
                        pageStates.checkedRows?.isSameTypeJobs ||
                        totalLoading.job
                      }
                    >
                      Create Invoice
                    </Button>
                  )}
                  <ExportDataButton
                    onClick={onExport}
                    disabled={isExportDisabled}
                  />
                </div>
              }
              className={`clientBillingCard table-pagination collapse-card ${LayoutCss.appCard}`}
            >
              <JobBillingsTable
                clientId={clientId}
                clientName={clientName}
                pageStates={pageStates}
                setPageStates={setPageStates}
                setRowData={setRowData}
                setTotalLoading={setTotalLoading}
                onBillingSelected={onBillingSelected}
                gridRef={jobGridRef}
                setIsExportDisabled={setIsExportDisabled}
              />
            </Card>
          </Spin>
        </Collapse.Panel>
      </Collapse>

      <Modal
        title="Create Invoice"
        className="s2-theme-style"
        open={pageStates.isDueDateModalVisible}
        destroyOnClose
        width={500}
        onOk={form.submit}
        okText="Create Invoice"
        okButtonProps={{ loading }}
        onCancel={() =>
          setPageStates((prev) => ({ ...prev, isDueDateModalVisible: false }))
        }
      >
        <CreateInvoice
          form={form}
          invoicePayloadRef={invoicePayloadRef}
          setLoading={setLoading}
          setPageStates={setPageStates}
        />
      </Modal>
      <Modal
        title="Create Invoice"
        className="s2-theme-style"
        open={pageStates.isJobDueDateModalVisible}
        destroyOnClose
        width={1200}
        onOk={jobForm.submit}
        okText="Create Invoice"
        okButtonProps={{ loading: jobLoading }}
        onCancel={() =>
          setPageStates((prev) => ({ ...prev, isJobDueDateModalVisible: false }))
        }
      >
        <CreateJobInvoice
          form={jobForm}
          invoicePayloadRef={invoicePayloadRef}
          pageStates={pageStates}
          setLoading={setJobLoading}
          setPageStates={setPageStates}
        />
      </Modal>
      <Modal
        centered
        width={1200}
        className="clientBillingModal modal-overflow-auto"
        title="WARNING: OFFERED SALARY MISSING"
        footer={false}
        open={pageStates.isOfferedSalaryModalVisible}
        onOk={() =>
          setPageStates((prev) => ({
            ...prev,
            isOfferedSalaryModalVisible: false,
          }))
        }
        onCancel={() => {
          setPageStates((prev) => ({
            ...prev,
            isOfferedSalaryModalVisible: false,
          }));
          salaryForm.resetFields();
          onCancelOfferSalary();
        }}
        closeIcon={
          <span className="closeIcon">
            <i className="icon-closeable" />
          </span>
        }
      >
        <OfferedSalaryCalculations
          reCalculateBilling={reCalculateBilling}
          pageStates={pageStates}
          salaryForm={salaryForm}
          rowData={rowData}
          setRowData={setRowData}
          setPageStates={setPageStates}
        />
      </Modal>
    </>
  );
}
